import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Tag,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Statistic,
  Rate,
  Select,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import apiService from '../../../../services/apiService';
import VendorForm from './VendorForm';

const { Title, Text } = Typography;

interface Vendor {
  id: string;
  name: string;
  code: string;
  email?: string;
  phone?: string;
  address?: string;
  contactPerson?: string;
  paymentTerms?: string;
  status: string;
  rating: number;
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

const VendorList: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [formVisible, setFormVisible] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    blacklisted: 0,
  });

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await apiService.get('/accounting/vendors');
      const data = response.vendors || response.data || [];
      setVendors(data);

      // Calculate statistics
      const total = data.length;
      const active = data.filter((v: Vendor) => v.status === 'ACTIVE').length;
      const inactive = data.filter((v: Vendor) => v.status === 'INACTIVE').length;
      const blacklisted = data.filter((v: Vendor) => v.status === 'BLACKLISTED').length;
      setStats({ total, active, inactive, blacklisted });
    } catch (error) {
      console.error('Failed to load vendors:', error);
      message.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleCreate = () => {
    setEditingVendor(null);
    setFormVisible(true);
  };

  const handleEdit = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setFormVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await apiService.delete(`/accounting/vendors/${id}`);
      message.success('Vendor deleted successfully');
      fetchVendors();
    } catch (error) {
      console.error('Failed to delete vendor:', error);
      message.error('Failed to delete vendor');
    }
  };

  const handleFormSuccess = () => {
    setFormVisible(false);
    setEditingVendor(null);
    fetchVendors();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'green';
      case 'INACTIVE':
        return 'orange';
      case 'BLACKLISTED':
        return 'red';
      default:
        return 'default';
    }
  };

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = !searchText ||
      vendor.name.toLowerCase().includes(searchText.toLowerCase()) ||
      vendor.code.toLowerCase().includes(searchText.toLowerCase()) ||
      vendor.email?.toLowerCase().includes(searchText.toLowerCase()) ||
      vendor.contactPerson?.toLowerCase().includes(searchText.toLowerCase());

    const matchesStatus = !statusFilter || vendor.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const columns: ColumnsType<Vendor> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 100,
      fixed: 'left',
      render: (code: string) => <Text strong>{code}</Text>,
    },
    {
      title: 'Vendor Name',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (name: string, record: Vendor) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {record.contactPerson && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <UserOutlined /> {record.contactPerson}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 200,
      render: (_, record: Vendor) => (
        <Space direction="vertical" size={0}>
          {record.email && <Text style={{ fontSize: 12 }}>{record.email}</Text>}
          {record.phone && <Text type="secondary" style={{ fontSize: 12 }}>{record.phone}</Text>}
        </Space>
      ),
    },
    {
      title: 'Payment Terms',
      dataIndex: 'paymentTerms',
      key: 'paymentTerms',
      width: 120,
      render: (terms: string) => terms || '-',
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 150,
      render: (rating: number) => (
        <Rate disabled defaultValue={rating} allowHalf style={{ fontSize: 14 }} />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record: Vendor) => (
        <Space size={4}>
          <Button
            type="default"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Vendor"
            description="Are you sure you want to delete this vendor?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="default" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Vendors"
              value={stats.total}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active"
              value={stats.active}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Inactive"
              value={stats.inactive}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Blacklisted"
              value={stats.blacklisted}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <ShopOutlined />
            <span>Vendor Management</span>
          </Space>
        }
        extra={
          <Space>
            <Input
              placeholder="Search vendors..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: 120 }}
              options={[
                { label: 'Active', value: 'ACTIVE' },
                { label: 'Inactive', value: 'INACTIVE' },
                { label: 'Blacklisted', value: 'BLACKLISTED' },
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchVendors} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Add Vendor
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredVendors}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} vendors`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>

      <VendorForm
        visible={formVisible}
        vendor={editingVendor}
        onCancel={() => {
          setFormVisible(false);
          setEditingVendor(null);
        }}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};

export default VendorList;
